import { CircleLoader } from '@/components/Loaders/CircleLoader';
import ArrowDownIcon from '@/images/arrows/ArrowDown.svg';
import ArrowUpIcon from '@/images/arrows/ArrowUp.svg';
import theme from '@/theme';
import { ITranslation, TranslationStatus } from '@d-id/types';
import styled from '@emotion/styled';
import MenuItem from '@mui/material/MenuItem';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import { useState } from 'react';
import { ORIGINAL } from '../../constants';
import { getBadgeIconFromLocale } from '../../utils';

const StyledStartAdornment = styled.span`
    display: inline-block;
    margin-right: 8px;
`;

const StyledSelect = styled(Select)<{ value: string[] }>`
    width: 180px;
    padding: 0 16px !important;
    color: ${theme.colors.secondary[700]};
    height: 38px;
    border-radius: 12px;
    text-transform: capitalize;

    .MuiSelect-select {
        padding: 0 !important;
        display: flex;
        align-items: center;
    }
` as unknown as typeof Select;

const StyledMenuItem = styled(MenuItem)`
    margin: 6px 8px;
    padding: 0 12px;
    border-radius: 8px;
    align-items: center;
    text-transform: capitalize;

    &.Mui-selected {
        background-color: transparent;

        :hover {
            background-color: rgba(0, 0, 0, 0.04);
        }
    }
`;

const Image = styled.img`
    width: 24px;
    height: 24px;
    cursor: pointer;
`;

interface Props {
    idTranslationsMap: Record<string, ITranslation>;

    setSelectedTranslationId: (value?: string) => void;
    selectedTranslationId?: string;
}

export default function PlayModalLanguageSelect({ idTranslationsMap, setSelectedTranslationId, selectedTranslationId }: Props) {
    const translationLoadingStatuses = [
        TranslationStatus.Started,
        TranslationStatus.Ready,
        TranslationStatus.Created,
        TranslationStatus.Validating,
    ];
    const [isOpen, setIsOpen] = useState(false);

    const handleChange = (event: SelectChangeEvent<string>) => {
        const selected = event.target.value;
        setSelectedTranslationId(selected);
        setIsOpen(false);
    };

    const handleToggleOpen = () => {
        setIsOpen(!isOpen);
    };

    if (!idTranslationsMap) {
        return null;
    }

    const sortedTranslations = Object.values(idTranslationsMap).sort((a, b) => {
        const getStatusPriority = (status: TranslationStatus) => {
            if (status === TranslationStatus.Done) {
                return 1;
            }
            if (translationLoadingStatuses.includes(status)) {
                return 2;
            }
            return 3;
        };

        const statusPriorityA = getStatusPriority(a.status);
        const statusPriorityB = getStatusPriority(b.status);

        // Compare status priority: 'done' first, then 'loading', then others
        if (statusPriorityA !== statusPriorityB) {
            return statusPriorityA - statusPriorityB;
        }

        if (a.language === ORIGINAL) {
            return -1;
        }

        if (b.language === ORIGINAL) {
            return 1;
        }

        return a.language.localeCompare(b.language);
    });

    return (
        <StyledSelect
            labelId="play-modal-lang-select-label"
            value={selectedTranslationId || ''}
            onChange={handleChange}
            open={isOpen}
            onOpen={handleToggleOpen}
            onClose={() => setIsOpen(false)}
            MenuProps={{ PaperProps: { sx: { borderRadius: '12px' } } }}
            IconComponent={() => <Image src={isOpen ? ArrowDownIcon : ArrowUpIcon} onClick={handleToggleOpen} />}>
            {sortedTranslations.map((item, index) => (
                <StyledMenuItem key={index} value={item.id} disabled={translationLoadingStatuses.includes(item.status)}>
                    <StyledStartAdornment>
                        {translationLoadingStatuses.includes(item.status) ? (
                            <CircleLoader height="30px" width="30px" />
                        ) : (
                            getBadgeIconFromLocale(item?.locale)
                        )}
                    </StyledStartAdornment>
                    {item.language}
                </StyledMenuItem>
            ))}
        </StyledSelect>
    );
}
